/**
 * Cookie + sessionStorage helpers.
 *
 * Pure utilities — they don't know about consent. The consent gate happens
 * one level up at the call site so we keep this module trivially testable.
 */

export interface CookieOptions {
	maxAgeSeconds?: number;
	domain?: string;
	path?: string;
	sameSite?: 'Lax' | 'Strict' | 'None';
	secure?: boolean;
}

export function readCookie(name: string): string | null {
	if (typeof document === 'undefined' || !document.cookie) return null;
	const parts = document.cookie.split('; ');
	for (const part of parts) {
		const idx = part.indexOf('=');
		const k = idx === -1 ? part : part.slice(0, idx);
		if (k === name) {
			const v = idx === -1 ? '' : part.slice(idx + 1);
			try {
				return decodeURIComponent(v);
			} catch {
				return v;
			}
		}
	}
	return null;
}

export function writeCookie(name: string, value: string, opts: CookieOptions = {}): void {
	if (typeof document === 'undefined') return;
	const segments: string[] = [`${name}=${encodeURIComponent(value)}`];
	segments.push(`path=${opts.path || '/'}`);
	if (opts.maxAgeSeconds != null) segments.push(`max-age=${opts.maxAgeSeconds}`);
	if (opts.domain) segments.push(`domain=${opts.domain}`);
	segments.push(`SameSite=${opts.sameSite || 'Lax'}`);
	const secure = opts.secure != null ? opts.secure : typeof location !== 'undefined' && location.protocol === 'https:';
	if (secure) segments.push('Secure');
	document.cookie = segments.join('; ');
}

export function deleteCookie(name: string, opts: CookieOptions = {}): void {
	writeCookie(name, '', { ...opts, maxAgeSeconds: 0 });
}

/** Best-effort detection of an eTLD+1 to share cookies across subdomains. */
export function inferCookieDomain(): string | undefined {
	if (typeof location === 'undefined') return undefined;
	const host = location.hostname || '';
	if (!host || host === 'localhost' || /^\d+\.\d+\.\d+\.\d+$/.test(host)) return undefined;
	const parts = host.split('.');
	if (parts.length < 2) return undefined;
	return '.' + parts.slice(-2).join('.');
}

export function readSession(key: string): string | null {
	try {
		return typeof sessionStorage !== 'undefined' ? sessionStorage.getItem(key) : null;
	} catch {
		return null;
	}
}

export function writeSession(key: string, value: string): void {
	try {
		if (typeof sessionStorage !== 'undefined') sessionStorage.setItem(key, value);
	} catch {
		// Quota / disabled storage — swallow per spec §10 (privacy-friendly fail).
	}
}
